/* eslint-disable @typescript-eslint/camelcase */
import get from 'lodash.get';
import { Node__NewsConnection, Node__NewsEdge, Node__News } from '../../graphql/types';
import { FileImageSharp } from '../types/api';
import { BlogSections, Blog } from '@content/types/blog';
import languages from '@content/json/languages.json';

const getRelatedArticle = (article: any) => {
  const fileImageHero = get(article.relationships, 'field_news_image') as FileImageSharp;

  return {
    title: get(article, 'title', ''),
    categories:
      article.relationships && article.relationships.field_news_categories
        ? [article.relationships.field_news_categories.name || '']
        : [],
    tags: get(article, 'relationships.field_news_tags', []).map(tag => tag.name) || [],
    date: get(article, 'created', ''),
    image: fileImageHero ? fileImageHero.localFile : null,
    introduction: get(article, 'field_news_body', ''),
    timeToRead: get(article, 'field_news_reading_time', ''),
    related: true,
  };
};

export const transformNews = (news?: Node__NewsConnection | null): Blog[] => {
  if (!news) {
    return [];
  }

  const data = get(news, 'edges', []).map((edge: Node__NewsEdge) => edge.node) as Node__News[];

  const allNews = data.map((news: Node__News) => {
    const sectionsList: BlogSections[] = [];
    const fileImageHero = get(news.relationships, 'field_news_image') as FileImageSharp;
    const relatedArticles = [];
    const relatedArticle1 = get(news.relationships, 'field_related_news_1', null);
    const relatedArticle2 = get(news.relationships, 'field_related_news_2', null);
    const relatedArticle3 = get(news.relationships, 'field_related_news_3', null);

    if (relatedArticle1) {
      relatedArticles.push(getRelatedArticle(relatedArticle1));
    }
    if (relatedArticle2) {
      relatedArticles.push(getRelatedArticle(relatedArticle2));
    }
    if (relatedArticle3) {
      relatedArticles.push(getRelatedArticle(relatedArticle3));
    }

    return {
      seo: {
        title: get(news, 'field_meta_title', ''),
        description: get(news, 'field_meta_description', ''),
        image: get(news, 'relationships.field_news_image.localFile.publicURL'),
      },
      langcode: get(news, 'langcode', ''),
      title: get(news, 'title', ''),
      categories:
        news.relationships && news.relationships.field_news_categories
          ? [news.relationships.field_news_categories.name || '']
          : [],
      tags: get(news, 'relationships.field_news_tags', []).map(tag => tag.name) || [],
      date: get(news, 'created', ''),
      dateModified: get(news, 'changed', ''),
      slug: news.path ? news.path.alias || news.id : news.id,
      image: fileImageHero ? fileImageHero.localFile : null,
      introduction: get(news, 'field_news_body', ''),
      timeToRead: get(news, 'field_news_reading_time', ''),
      related: get(news, 'status', false),
      relatedArticles,
      sections: get(news, 'relationships.field_news_components', []).map(section => {
        if (section.internal && section.internal.type === 'paragraph__news_image_title_text') {
          const fileImage = get(section.relationships, 'field_nitt_image') as FileImageSharp;

          sectionsList.push({
            content: section.field_nitt_text || '',
            title: section.field_nitt_title || '',
            image: fileImage ? fileImage.localFile : null,
            type: 'image-text',
          });
        } else if (section.internal && section.internal.type === 'paragraph__news_text') {
          sectionsList.push({
            content: section.field_nt_text || '',
            type: 'text',
          });
        } else if (section.internal && section.internal.type === 'paragraph__news_quotation') {
          sectionsList.push({
            content: section.field_nq_quotation || '',
            type: 'quote',
          });
        } else if (section.internal && section.internal.type === 'paragraph__news_cta') {
          sectionsList.push({
            buttonHref: section.field_nc_button_url,
            buttonLabel: section.field_nc_button_text,
            content: section.field_nc_text || '',
            type: 'button-text',
          });
        } else if (section.internal && section.internal.type === 'paragraph__video') {
          const fileImage = get(section.relationships, 'field_thumbnail') as FileImageSharp;

          sectionsList.push({
            title: section.field_header,
            content: section.field_video_text ? section.field_video_text.value : '',
            image: fileImage ? fileImage.localFile : null,
            video: section.field_video_video,
            type: 'video',
          });
        }

        return sectionsList;
      })[0],
    };
  });

  const allNewsLanguages = {};

  languages.codes.map(lang => {
    allNewsLanguages[lang] = allNews.filter(news => news.langcode === lang);
  });

  return allNewsLanguages;
};

export const transformNewsSettings = newsSettings => {
  const newsSettingsLanguages = {};

  newsSettings.edges.forEach(edge => {
    const data = edge.node;

    newsSettingsLanguages[data.langcode] = {
      button1: get(data, 'field_settings_button_text', ''),
      button2: get(data, 'field_settings_button_text_2', ''),
      text1: get(data, 'field_settings_text_1', ''),
      text2: get(data, 'field_settings_text_2', ''),
      text3: get(data, 'field_settings_text_3', ''),
      text4: get(data, 'field_settings_text_4', ''),
      text5: get(data, 'field_settings_text_5', ''),
      text6: get(data, 'field_settings_text_6', ''),
      text7: get(data, 'field_settings_text_7', ''),
      tresc: get(data, 'field_settings_tresc.value', ''),
      seo: {
        title: get(data, 'field_meta_title', ''),
        description: get(data, 'field_meta_description', ''),
        image: get(data, 'relationships.field_main_image.localFile.publicURL'),
      },
    };
  });

  return newsSettingsLanguages;
};
